import { addDays, addYears, differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';

import { Account, AccountGroup, CashFlowConfig, CashFlowType } from '@ndvr/portfolio-api';

import {
    getNextCashFlowOccurrence,
    getNextWithdrawal,
} from 'views/portfolio/PortfolioPage/tabs/liquidity-planning/cash-flow-schedule';
import { getFactorOccurrences } from 'views/portfolio/PortfolioPage/utils/factor-date-frame-utils';

export const hasIncompleteInflation = ({ inflationConfig: inflation }: CashFlowConfig) =>
    inflation.inflationAdjusted && (inflation.inflationPercent == null || !inflation.inflationStartDate);

/** The withdrawals that belong to these accounts' policy: pinned to one of them, or portfolio-level when the group
 * shares an allocation. */
export const getPolicyWithdrawals = (
    configs: CashFlowConfig[],
    accounts: Account[],
    accountGroup?: Pick<AccountGroup, 'accountGroupGUID' | 'allocationGUID'>,
) => {
    const accountGUIDs = new Set(accounts.map((account) => account.accountGUID));
    const portfolioLevel = !!accountGroup?.allocationGUID;
    return configs.filter(
        (config) =>
            config.cashFlowType === CashFlowType.WITHDRAWAL &&
            (config.accountGUID
                ? accountGUIDs.has(config.accountGUID)
                : portfolioLevel && config.accountGroupGUID === accountGroup?.accountGroupGUID),
    );
};

export const getNextPolicyWithdrawal = (
    configs: CashFlowConfig[],
    accounts: Account[],
    accountGroup?: Pick<AccountGroup, 'accountGroupGUID' | 'allocationGUID'>,
    today?: Date,
) => getNextWithdrawal(getPolicyWithdrawals(configs, accounts, accountGroup), today);

// The withdrawals with an occurrence still to come, earliest start first: how the Withdrawal Plan lists them.
export const upcomingPolicyWithdrawals = (configs: CashFlowConfig[], today: Date) =>
    configs
        .filter((config) => getNextCashFlowOccurrence(config, today) !== undefined)
        .sort((a, b) => a.factorDateFrame.startDate.localeCompare(b.factorDateFrame.startDate));

export const withdrawalTitle = (config: CashFlowConfig) => config.title.plainText || 'Unnamed withdrawal';

/** The account a withdrawal is drawn from; one without an account can come from any of them. */
export const withdrawalAccountName = (accounts: Account[], accountGUID?: string) =>
    accountGUID
        ? (accounts.find((item) => item.accountGUID === accountGUID)?.accountName.plainText ?? 'Unknown account')
        : 'Any Account';

// Why totalPolicyWithdrawals came out NaN: the withdrawals whose inflation settings are incomplete.
export const incompleteInflationNote = (withdrawals: CashFlowConfig[]) => {
    const titles = withdrawals.filter(hasIncompleteInflation).map(withdrawalTitle);
    return `Inflation settings are incomplete for: ${titles.join(', ')}`;
};

// Match CashFlowCalculator's calendar-year inflation and nearest-$25 rounding.
// These are net scheduled withdrawals, not tax-grossed cash-raising requirements.
export const projectedWithdrawalAmount = (config: CashFlowConfig, date: string): number => {
    const amount = Number(config.amount);
    const inflation = config.inflationConfig;
    if (!inflation.inflationAdjusted) return amount;
    if (hasIncompleteInflation(config)) return NaN;
    const start = parseISO(inflation.inflationStartDate!.slice(0, 10));
    const year = parseISO(date).getFullYear();
    const rate = Number(inflation.inflationPercent) / 100;
    if (rate === 0 || year <= start.getFullYear()) return amount;
    const initialDays = differenceInCalendarDays(new Date(start.getFullYear() + 1, 0, 1), start);
    const multiplier = Number(
        ((1 + (rate * initialDays) / 365.25) * (1 + rate) ** (year - start.getFullYear() - 1)).toFixed(8),
    );
    const inflated = Number((amount * multiplier).toFixed(4));
    if (inflated === amount) return amount;
    const rounded = Math.round(inflated / 25) * 25;
    return multiplier < 1 ? Math.min(rounded, amount) : Math.max(rounded, amount);
};

/** Nominal projected withdrawals from today up to, but not including, `to`. */
export const totalPolicyWithdrawalsUntil = (configs: CashFlowConfig[], to: Date, today = new Date()) => {
    const from = startOfDay(today);
    return configs.reduce(
        (total, config) =>
            total +
            getFactorOccurrences(config.factorDateFrame, from, to).reduce(
                (sum, date) => sum + projectedWithdrawalAmount(config, date),
                0,
            ),
        0,
    );
};

export const totalPolicyWithdrawals = (configs: CashFlowConfig[], years: number, today = new Date()) =>
    totalPolicyWithdrawalsUntil(configs, addYears(startOfDay(today), years), today);

/**
 * The plan's projection horizon: today until the target end date (inclusive), the same span Lifestyle protection
 * secures. Without a target end date it falls back to the next 12 months.
 */
export const policyHorizon = (configs: CashFlowConfig[], targetEndDate: string | undefined, today: Date) => {
    if (!targetEndDate) return { total: totalPolicyWithdrawals(configs, 1, today), endLabelDate: undefined };
    const end = addDays(parseISO(targetEndDate.replace(/Z$/, '')), 1);
    return { total: totalPolicyWithdrawalsUntil(configs, end, today), endLabelDate: targetEndDate };
};
